import React, { FC, useState } from 'react';
import TextInput from 'components/Elements/TextInput';
import { SearchIcon } from 'components/Elements/CustomSVGIcon';
import { NsipVisitorSearchResult } from 'core/accessRequestForm/types';
import FormSection from '../FormSection';
import './nsipVisitorSearch.scss';

interface NsipVisitorSearchProps {
    onSearch: (query: string) => void;
    onSelect: (visitor: NsipVisitorSearchResult) => void;
    onClickEnterManually: () => void;
    searchResults: NsipVisitorSearchResult[];
    isSearching: boolean;
    selectedNsipIds: number[];
}

const NsipVisitorSearch: FC<NsipVisitorSearchProps> = ({
    onSearch,
    onSelect,
    onClickEnterManually,
    searchResults,
    isSearching,
    selectedNsipIds,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [hasSearched, setHasSearched] = useState(false);

    const canSearch = searchTerm.length >= 3 && !isSearching;

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleSearch = () => {
        if (canSearch) {
            setHasSearched(true);
            onSearch(searchTerm);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const searchIcon = (
        <div
            className={`nsip-search-icon-button ${!canSearch ? 'disabled' : ''}`}
            onClick={handleSearch}
            role="button"
            tabIndex={0}
            aria-label="Search visitors"
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}>
            <SearchIcon />
        </div>
    );

    return (
        <div className="nsip-visitor-search-container">
            <FormSection title="Search Visitors">
                <div className="nsip-search-input-wrapper">
                    <TextInput
                        leftIcon={searchIcon}
                        value={searchTerm}
                        placeholder="Search"
                        onChange={handleSearchChange}
                        onKeyDown={handleKeyDown} />
                </div>
                {hasSearched && !isSearching && (
                    <div className="results-container">
                        {searchResults.length > 0 ? (
                            <ul className="nsip-results-list">
                                {searchResults.map((visitor) => {
                                    const isSelected = selectedNsipIds.includes(visitor.id);
                                    return (
                                        <li key={visitor.id}>
                                            <div
                                                className={`nsip-result-item ${isSelected ? 'item-selected' : ''}`}
                                                role="button"
                                                tabIndex={isSelected ? -1 : 0}
                                                aria-disabled={isSelected}
                                                // Already-added visitors can't be selected again.
                                                onClick={() => { if (!isSelected) onSelect(visitor); }}
                                                onKeyDown={(e) => { if (!isSelected && (e.key === 'Enter' || e.key === ' ')) onSelect(visitor); }}>
                                                <span className="result-name">
                                                    {visitor.name}
                                                    {visitor.nsipReference && (
                                                        <span className="result-nsip-ref"> · {visitor.nsipReference}</span>
                                                    )}
                                                    {visitor.isExpired && (
                                                        <span className="result-expired"> (Expired)</span>
                                                    )}
                                                </span>
                                                <div className="result-bottom">
                                                    <span className="result-company">{visitor.companyName || ''}</span>
                                                    <span className="result-email">{visitor.email || ''}</span>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : (
                            <div className="no-results">No visitors found</div>
                        )}
                    </div>
                )}
                <div className="button-wrapper">
                    <button onClick={onClickEnterManually} type="button">Enter Visitor Details Manually</button>
                </div>
            </FormSection>
        </div>
    );
};

export default NsipVisitorSearch;
